/**
 * Telnyx port-pending gating predicates (CON-1488).
 *
 * Telnyx port-first users wait 1-2 days on a port-out PIN from their
 * current carrier. The new flow lets them save the port tab as a draft
 * (no Telnyx API call), then immediately complete TCR registration in
 * parallel, then return with the PIN to fire the actual port. This file
 * pins the gating rules as pure functions so RedirectLogic.jsx and
 * DashboardLayoutTopMessage.jsx can call into one source of truth and
 * unit-tested predicates can verify the rules without rendering React.
 *
 *   isTelnyxPrePort()             — hard-locked to Port tab BEFORE draft saved.
 *                                    The starting state for a Telnyx port-first user.
 *   isTelnyxPortDraftSaved()      — draft saved, no PIN yet. User is in the
 *                                    "complete TCR in parallel + wait for PIN" phase.
 *   shouldShowPortInProgressBanner() — banner visible whenever a draft exists
 *                                    but the port hasn't been submitted yet.
 *   doesUserHaveTextingPhoneNumber() — convenience: !!userPhoneNumbersInfo.phoneNumber.
 *
 * Lumian / Twilio paths: every predicate that triggers new behavior also
 * checks gatedProvider === 'Telnyx' so existing flows are byte-for-byte
 * unchanged.
 *
 * STALENESS CAVEAT (Sonya Eckel sp 45, 2026-08-03): every predicate here
 * is a pure read of the `user` object the CALLER passes in — almost always
 * state.account.user, which only refreshes on GET_ACCOUNT_INFO
 * (/api/account/me), LOGIN, SILENT_LOGIN, or sign-in-as. These predicates
 * cannot defend each other: a dispatcher gate (RedirectLogic) and a render
 * gate (ChooseNewOrPortModal) reading the same stale object fail together,
 * which is exactly how the May-2026 belt-and-suspenders fix was bypassed.
 * Do not add another same-field check as a "defensive layer" — instead make
 * sure any mutation that changes the underlying viewmodel fields
 * (hasSavedPortDraft / hasSubmittedPortRequest / hasPortInFlight /
 * userPhoneNumbersInfo) is followed by dispatch(getAccountInfo()).
 */

const TELNYX = 'Telnyx';

const doesUserHaveTextingPhoneNumber = (user) => !!user?.userPhoneNumbersInfo?.phoneNumber;

// Hard lock to Port tab: a Telnyx-gated user who has neither acquired a
// number nor saved a port draft nor submitted a port yet. They MUST
// either choose a new number or fill out the port form before they can
// do anything else.
const isTelnyxPrePort = (user) => {
  if (!user) return false;
  return user.gatedProvider === TELNYX
    && !doesUserHaveTextingPhoneNumber(user)
    && !user.hasSavedPortDraft
    && !user.hasSubmittedPortRequest;
};

// Draft saved, port not yet submitted (no PIN entered). This is the
// state where the user is unlocked from the Port-tab lock and can
// proceed to TCR — and where the persistent header banner shows so
// they can find their way back to enter the PIN.
const isTelnyxPortDraftSaved = (user) => {
  if (!user) return false;
  return user.gatedProvider === TELNYX
    && !!user.hasSavedPortDraft
    && !user.hasSubmittedPortRequest;
};

// Banner visibility: show whenever a draft exists AND the port hasn't
// been submitted. Disappears the instant the user enters the PIN and
// the row transitions to status='submitted'. Non-dismissible.
// Not for a queued Lumian v2 draft (hasLumianV2PortDraft): it already has
// its PIN and waits only on TCR approval, so "enter your port PIN" is wrong.
const shouldShowPortInProgressBanner = (user) => {
  if (!user) return false;
  return !!user.hasSavedPortDraft && !user.hasSubmittedPortRequest
    && !user.hasLumianV2PortDraft;
};

// True when a Telnyx user's port is handed to the carrier but not yet
// completed (server sets hasPortInFlight for status submitted/pending/
// scheduled — NOT completed, a completed porter has a number). Broadened
// past the draft-only gates for Tina Glotzbach (June 2026): her port
// advanced to 'pending' with TCR incomplete, and every draft-only check
// stopped treating her as port-pending, blocking her from the TCR flow.
const isTelnyxPortInFlight = (user) => {
  if (!user) return false;
  return user.gatedProvider === TELNYX && !!user.hasPortInFlight;
};

// Scenario 2 TCR-tab-lock gating helper. Today's rule:
//   doesUserHaveTextingPhoneNumber && needsTCRRegistration(tcr)
// New rule for Telnyx port-pending users: TCR tab also activates when a
// draft OR in-flight (submitted/pending/scheduled) port row exists, even
// without a phoneNumber (the texting number will arrive post-port). The
// draft check is kept alongside hasPortInFlight so draft users stay
// covered even if the server has not yet deployed the newer field.
// Lumian/Twilio path unchanged because both new branches require
// gatedProvider === 'Telnyx'.
const shouldLockToTCRTab = (user) => {
  if (!user) return false;
  const hasPhone = doesUserHaveTextingPhoneNumber(user);
  const telnyxPortDraftActive = user.gatedProvider === TELNYX && !!user.hasSavedPortDraft;
  return hasPhone || telnyxPortDraftActive || isTelnyxPortInFlight(user);
};

// Post-decision gate (CON-1488): true when a Telnyx user has already
// resolved their phone-number path — they have a texting number, a saved
// port draft, OR a submitted/completed port. By the time any Telnyx user
// reaches TCR, the pre-number decision flow guarantees one of these is true
// (RedirectLogic Scenario 2 / shouldLockToTCRTab only routes Telnyx users to
// TCR when hasPhone || hasSavedPortDraft). Used to suppress the "choose new
// number or port" decision section in the post-TCR-submit modal — that
// decision is already made and re-prompting would navigate them away from
// the PIN-entry they need. Lumian/Twilio users never set
// gatedProvider==='Telnyx', so this is always false for them and they keep
// seeing the decision section exactly as today.
const isTelnyxNumberPathResolved = (user) => {
  if (!user) return false;
  return user.gatedProvider === TELNYX
    && (doesUserHaveTextingPhoneNumber(user)
      || !!user.hasSavedPortDraft
      || !!user.hasSubmittedPortRequest);
};

// True when a Telnyx user has a committed/in-flight port (status one of
// 'submitted','pending','scheduled','completed' on the server — see
// convertUserToUserViewModel.js committedPortStatuses). Used to suppress
// the "Choose a New Number" modal for users mid-port who have neither a
// live UPNI phoneNumber yet nor a draft (Kristen Paul, sp 1499, June
// 2026 — port pending at Telnyx with an empty placeholder UPNI row,
// modal was wrongly offered and would have provisioned a new number).
// For long-completed ports the decision-gate short-circuits earlier on
// userTextingPhoneNumber being truthy, so this predicate adds no
// suppression risk for established users.
const isTelnyxPortCommitted = (user) => {
  if (!user) return false;
  return user.gatedProvider === TELNYX && !!user.hasSubmittedPortRequest;
};

module.exports = {
  doesUserHaveTextingPhoneNumber,
  isTelnyxPrePort,
  isTelnyxPortDraftSaved,
  isTelnyxPortCommitted,
  isTelnyxPortInFlight,
  isTelnyxNumberPathResolved,
  shouldShowPortInProgressBanner,
  shouldLockToTCRTab,
};
